import React from 'react';

import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import getArchiveAwareDisplayName from 'archive/getArchiveAwareDisplayName';
import {Location, ParentStorableData} from 'constants/SampleConstants';
import * as Context from 'Context';
import {TagUpdateOptions} from 'file-browser/FinderActions';
import {Tag} from 'folder-items/FileConstants';
import {ActionMethod, ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {writeQuery} from 'graphql-client/ApolloWrappers';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ParentStorableValidationErrors from 'inventory/location-validation/ParentStorableValidationErrors';
import PrintLabelIcon from 'label-printing/PrintLabelIcon';
import {optimisticallyUpdate} from 'lib/OptimisticallyUpdate';
import {SectionWrapper} from 'metadata/MetadataEditorInfoSection';
import MetadataTagTable from 'metadata/MetadataTagTable';
import notifications from 'notifications';
import BarcodeValue, {BarcodeValueProps} from 'samples/BarcodeValue';
import {LocationCapacityConfigurationParams, UpdateLocationParams} from 'samples/LocationDAO';
import {LocationViewQuery, LocationViewQueryVariables} from 'samples/locations/__generated/LocationViewQuery';
import LocationToolbar from 'samples/locations/LocationToolbar';
import {
  LOCATION_VIEW_QUERY,
  LocationInfo,
  translateFromLocationInfo,
} from 'samples/locations/LocationViewConstants';
import SampleCreatorInfo from 'samples/SampleCreatorInfo';
import * as SampleUtil from 'samples/SampleUtil';
import {getAvailableSpaceDisplayString, getPossibleEditBarcodesDisabledError} from 'samples/SampleUtil';
import * as StorableActions from 'samples/StorableActions';
import EditableLocationAllowedChildSchemasField from 'samples/storables/editable-fields/EditableLocationAllowedChildSchemasField';
import {EditableLocationCapacityField} from 'samples/storables/editable-fields/EditableLocationCapacityField';
import StorableBanner from 'samples/storables/StorableBanner';
import StorableBreadcrumbs from 'samples/storables/StorableBreadcrumbs';
import {getTagTableFriendlyTags} from 'schemas/tags/TagUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import * as Headers from 'ui-library/Headers';
import {EditableBooleanDropdownField} from 'ui-library/inputs/EditableBooleanDropdownField';
import EditableTextField from 'ui-library/inputs/EditableTextField';
import {readableTime} from 'util/DateUtil';
import * as Modals from 'util/Modals';

interface LocationViewProps {
  location: LocationInfo;
  reload: () => void;
}

/**
 * Location metadata page, presentation layer.
 *
 * Does optimistic updates when making changes wherever possible.
 */
export default function LocationView(props: LocationViewProps): JSX.Element {
  const ACTION_ORIGIN = ActionOrigin.INVENTORY_LOCATION_PAGE;

  async function updateLocationWithDataModsWithAnalyticsContext(
    locationFields: UpdateLocationParams,
    actionMethod: ActionMethod | null,
    eventGroupId?: string,
    onFinish?: (errorMessage?: string) => void
  ): Promise<Location | null> {
    return await Context.usingAsync(
      {
        analyticsMetadata: {
          actionOrigin: ACTION_ORIGIN,
          actionMethod,
          eventGroupId,
        },
      },
      null,
      async () => {
        const rv: Location | null = await StorableActions.updateLocationWithDataMods(
          props.location.id,
          locationFields,
          undefined,
          onFinish
        );
        props.reload();
        return rv;
      }
    );
  }

  async function updateLocationAndReportError(
    doUpdate: () => Promise<Location | null>
  ): Promise<Location | null> {
    try {
      return await doUpdate();
    } catch (e) {
      const friendlyError = notifications.getFriendlyError(e);
      if (friendlyError != null) {
        notifications.error(friendlyError);
        return null;
      } else {
        throw e;
      }
    }
  }

  async function updateLocationWithOptimisticUpdate({
    locationFields,
    actionMethod,
    optimisticData,
    revertData,
  }: {
    locationFields: UpdateLocationParams;
    actionMethod: ActionMethod | null;
    optimisticData: LocationViewQuery;
    revertData: LocationViewQuery;
  }): Promise<void> {
    await updateLocationAndReportError(
      async () =>
        await optimisticallyUpdate({
          persistData: async () =>
            await updateLocationWithDataModsWithAnalyticsContext(locationFields, actionMethod),
          writeDataToCache: (data) => {
            writeQuery<LocationViewQuery, LocationViewQueryVariables>({query: LOCATION_VIEW_QUERY}, data, {
              id: props.location.id,
            });
          },
          optimisticData,
          revertData,
        })
    );
  }

  async function handleUpdateName(name: string): Promise<void> {
    await updateLocationWithOptimisticUpdate({
      locationFields: {name},
      actionMethod: ActionMethod.IN_LINE_EDIT,
      optimisticData: {
        location: translateFromLocationInfo({
          ...props.location,
          name,
        }),
      },
      revertData: {
        location: translateFromLocationInfo(props.location),
      },
    });
  }

  async function handleSetParentStorable(
    parentStorableData: ParentStorableData,
    eventGroupId?: string,
    onFinish?: (errorMessage?: string) => void
  ): Promise<void> {
    // Difficult to update optimistically here because we don't receive the info we need about the parent
    await updateLocationAndReportError(
      async () =>
        await updateLocationWithDataModsWithAnalyticsContext(
          {parentStorableData},
          ActionMethod.BUTTON_CLICK,
          eventGroupId,
          onFinish
        )
    );
  }

  async function handleUpdateTags(
    tagUpdates: TagUpdateOptions
  ): Promise<Array<{id: string; tags: Array<Tag>}> | null> {
    // Downstream components take care of optimistic updates here
    const {toSet} = tagUpdates;
    const location: Location | null | undefined = await updateLocationAndReportError(
      async () => await updateLocationWithDataModsWithAnalyticsContext({tags: toSet}, null)
    );
    return location != null
      ? [
          {
            id: location.id,
            tags: location?.tags
              ? getTagTableFriendlyTags(location?.tags)
              : getTagTableFriendlyTags(props.location.tags),
          },
        ]
      : null;
  }

  async function handleUpdateCapacityConfiguration(
    capacityConfig: LocationCapacityConfigurationParams
  ): Promise<void> {
    await updateLocationWithOptimisticUpdate({
      locationFields: capacityConfig,
      actionMethod: ActionMethod.IN_LINE_EDIT,
      optimisticData: {
        location: translateFromLocationInfo({
          ...props.location,
          ...capacityConfig,
        }),
      },
      revertData: {
        location: translateFromLocationInfo(props.location),
      },
    });
  }

  async function handleUpdateAllowedChildSchemas(allowedSchemaIds: Array<string> | null): Promise<void> {
    await handleUpdateCapacityConfiguration({allowedChildStorableSchemaApiIds: allowedSchemaIds});
  }

  async function handleUpdateAllowsNonLocationChildren(allowsNonLocationChildren: boolean): Promise<void> {
    if (
      !allowsNonLocationChildren &&
      (props.location.capacity != null || props.location.allowedChildStorableSchemaApiIds != null)
    ) {
      Modals.confirm(
        'Disallow plates, boxes, and containers?',
        'This will also clear the specified capacity and allowed inventory schemas.',
        ButtonStyle.NEGATIVE,
        {
          acceptText: 'Continue',
          enableRedesign: true,
        }
      ).done(() => {
        handleUpdateCapacityConfiguration({
          allowsNonLocationChildren,
          capacity: null,
          allowedChildStorableSchemaApiIds: null,
        });
      });
    } else {
      await handleUpdateCapacityConfiguration({allowsNonLocationChildren});
    }
  }

  async function handleUpdateCapacity(capacity: number | null): Promise<void> {
    await handleUpdateCapacityConfiguration({capacity});
  }

  async function handleUpdateBarcode(barcode: string): Promise<void> {
    await updateLocationWithOptimisticUpdate({
      locationFields: {barcode},
      actionMethod: ActionMethod.IN_LINE_EDIT,
      optimisticData: {
        location: translateFromLocationInfo({
          ...props.location,
          barcode,
        }),
      },
      revertData: {
        location: translateFromLocationInfo(props.location),
      },
    });
  }

  function getBarcodeValueProps(): BarcodeValueProps {
    return {
      storable: props.location,
      isDisabled: !props.location.permissions.canEditBarcodes,
      handleBarcodeChange: handleUpdateBarcode,
      disableEditsWithTooltip: getPossibleEditBarcodesDisabledError(
        props.location.permissions.canEditBarcodes
      ),
    };
  }

  const tagTableFriendlyTags = getTagTableFriendlyTags(props.location.tags);

  const locationCapacityFields = (
    <>
      <Row bufferRowReset={true}>
        <Col sm={6}>
          <SectionWrapper headerName="Allows plates, boxes, and containers" elementName="allowsChildren">
            <EditableBooleanDropdownField
              selectedValue={props.location.allowsNonLocationChildren}
              onChange={handleUpdateAllowsNonLocationChildren}
              shouldUseCompactDisplay={true}
              disabled={!props.location.permissions.writable}
              dataTestAttrs={{component: 'LocationCapacityConfig', element: 'allowsChildren'}}
            />
          </SectionWrapper>
        </Col>
        {props.location.allowsNonLocationChildren && (
          <Col sm={6}>
            <SectionWrapper headerName="Allowed inventory schemas" elementName="allowedSchemas">
              <EditableLocationAllowedChildSchemasField
                selectedSchemaIds={props.location.allowedChildStorableSchemaApiIds}
                onSetSelectedSchemaIds={handleUpdateAllowedChildSchemas}
                shouldUseCompactDisplay={true}
                registryId={props.location.storableSchema.registryId}
                isDisabled={!props.location.permissions.writable}
                dataTestAttrs={{component: 'LocationCapacityConfig', element: 'allowedSchemas'}}
              />
            </SectionWrapper>
          </Col>
        )}
      </Row>
      {props.location.allowsNonLocationChildren && (
        <Row bufferRowReset={true}>
          <Col sm={6}>
            <SectionWrapper headerName="Inventory capacity" elementName="capacity">
              <EditableLocationCapacityField
                capacity={props.location.capacity}
                onChange={handleUpdateCapacity}
                shouldUseCompactDisplay={true}
                disabled={!props.location.permissions.writable}
                dataTestAttrs={{component: 'LocationCapacityConfig', element: 'capacity'}}
              />
            </SectionWrapper>
          </Col>
          {props.location.capacity != null && props.location.occupiedCapacity != null && (
            <Col sm={6}>
              <SectionWrapper headerName="Available space" elementName="availableCapacity">
                <div {...getTestProps({component: 'LocationCapacityConfig', element: 'availableCapacity'})}>
                  {getAvailableSpaceDisplayString(props.location.capacity, props.location.occupiedCapacity)}
                </div>
              </SectionWrapper>
            </Col>
          )}
        </Row>
      )}
    </>
  );

  return (
    <div {...getTestProps({component: 'LocationView', element: 'root'})}>
      <StorableBanner isWritable={props.location.permissions.writable} storable={props.location} />
      <div className="react-tab-container">
        <div className="react-tab-container-narrowTab">
          <div className="u-lr-split metadata-header-section">
            <Headers.Header1>
              <EditableTextField
                isDisabled={!props.location.permissions.canEditNamesAndBarcodes}
                requestChange={handleUpdateName}
                value={props.location.name}
                disableEditsWithTooltip={SampleUtil.getPossibleEditNamesDisabledError(
                  props.location.permissions.canEditNamesAndBarcodes
                )}
                dataTestAttrs={{component: 'LocationView', element: 'name'}}
              />
              <ArchiveRecordLabel item={props.location} />
            </Headers.Header1>
            <LocationToolbar
              location={props.location}
              onSetParentStorable={handleSetParentStorable}
              reload={props.reload}
            />
          </div>
          <ParentStorableValidationErrors
            parentStorableId={props.location.id}
            registryId={props.location.storableSchema.registryId}
          />
          <Row bufferRowReset={true}>
            <Col sm={6}>
              <SectionWrapper headerName="Barcode" elementName="barcode">
                <div className="u-vertical-center-items">
                  <BarcodeValue {...getBarcodeValueProps()} />
                  <PrintLabelIcon itemIds={[props.location.id]} />
                </div>
              </SectionWrapper>
            </Col>
          </Row>
          <Row bufferRowReset={true}>
            <Col sm={6}>
              <SectionWrapper headerName="Location" elementName="location">
                {props.location.parentStorable && isInaccessible(props.location.parentStorable) ? (
                  <>No access</>
                ) : (
                  <StorableBreadcrumbs storable={props.location} />
                )}
              </SectionWrapper>
            </Col>
            <Col sm={6}>
              <SectionWrapper headerName="Schema" elementName="schema">
                <div>{getArchiveAwareDisplayName(props.location.storableSchema)}</div>
              </SectionWrapper>
            </Col>
          </Row>
          <Row bufferRowReset={true}>
            <Col sm={6}>
              <SectionWrapper headerName="Creator" elementName="creator">
                <SampleCreatorInfo creator={props.location.creator} />
              </SectionWrapper>
            </Col>
            <Col sm={6}>
              <SectionWrapper headerName="Created" elementName="createdAt">
                <span>{readableTime(props.location.createdAt)}</span>
              </SectionWrapper>
            </Col>
          </Row>
          {locationCapacityFields}
          <div className="u-spacing-top">
            <MetadataTagTable
              emptyMessage="This location has no schema fields."
              fileId={props.location.id}
              itemLinkAllowed={false}
              initialTags={tagTableFriendlyTags}
              areTagsEditable={props.location.permissions.writable}
              title="Location fields"
              updateTags={handleUpdateTags}
              showRequiredFieldErrors={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
